import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { InsuranceStatus, Page, RoadworthinessTest, Transfer, Vehicle } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['LTA_OFFICER'];

/** M21 — vehicle registry, ownership transfers, insurance status, roadworthiness history. */
export function Vehicles() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedRegNo, setSelectedRegNo] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/transport/vehicles?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<Vehicle>>(path);

  return (
    <div>
      <PageHeader
        title="Vehicle Registry"
        subtitle="Registration, ownership transfers, insurance status, and roadworthiness history (M21 — LTA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-52" placeholder="Reg no. or VIN…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {['ACTIVE', 'EXPORTED', 'SCRAPPED', 'STOLEN'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isOfficer ? (
              <button className="btn-primary" onClick={() => setShowRegister(true)}>
                + Register vehicle
              </button>
            ) : null}
          </>
        }
      />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No vehicles match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reg. no.</th>
                <th className="table-head">Make / model</th>
                <th className="table-head">Year</th>
                <th className="table-head">Owner</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((v) => (
                <tr key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedRegNo(v.regNo)}>
                  <td className="table-cell font-mono text-xs font-semibold">{v.regNo}</td>
                  <td className="table-cell">
                    {v.make} {v.model}
                  </td>
                  <td className="table-cell text-xs text-slate-500">{v.year}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {v.ownerType} · {v.ownerRef}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={v.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRegister ? (
        <RegisterVehicleModal
          onClose={() => setShowRegister(false)}
          onCreated={() => {
            setShowRegister(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedRegNo ? (
        <VehicleDetailModal
          regNo={selectedRegNo}
          canAct={isOfficer}
          onClose={() => setSelectedRegNo(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function RegisterVehicleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [regNo, setRegNo] = useState('');
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [ownerType, setOwnerType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [ownerRef, setOwnerRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/transport/vehicles', {
        regNo: regNo.trim(),
        vin: vin.trim(),
        make: make.trim(),
        model: model.trim(),
        year: Number.parseInt(year, 10),
        ownerType,
        ownerRef: ownerRef.trim(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register vehicle');
    } finally {
      setBusy(false);
    }
  };

  const valid = regNo.trim().length >= 2 && vin.trim().length >= 5 && make.trim() && model.trim() && ownerRef.trim().length >= 1;

  return (
    <Modal title="Register a vehicle" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Field label="Registration no.">
          <input className="field-input" value={regNo} onChange={(e) => setRegNo(e.target.value.toUpperCase())} placeholder="S 12345" />
        </Field>
        <Field label="VIN" className="col-span-2 sm:col-span-1">
          <input className="field-input" value={vin} onChange={(e) => setVin(e.target.value.toUpperCase())} />
        </Field>
        <Field label="Year">
          <input className="field-input" type="number" value={year} onChange={(e) => setYear(e.target.value)} />
        </Field>
        <Field label="Make">
          <input className="field-input" value={make} onChange={(e) => setMake(e.target.value)} placeholder="Toyota" />
        </Field>
        <Field label="Model">
          <input className="field-input" value={model} onChange={(e) => setModel(e.target.value)} placeholder="Hilux" />
        </Field>
        <Field label="Owner type">
          <select className="field-input" value={ownerType} onChange={(e) => setOwnerType(e.target.value as 'PERSON' | 'ENTITY')}>
            <option value="PERSON">Person (NIN)</option>
            <option value="ENTITY">Entity (business id)</option>
          </select>
        </Field>
        <Field label={ownerType === 'PERSON' ? 'Owner NIN' : 'Owner business id'} className="col-span-2">
          <input className="field-input" value={ownerRef} onChange={(e) => setOwnerRef(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Registering…' : 'Register vehicle'}
      </button>
    </Modal>
  );
}

type TransferAction = null | 'lookup' | 'create';

function VehicleDetailModal({
  regNo,
  canAct,
  onClose,
  onChanged,
}: {
  regNo: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const vehicleQ = useApiQuery<Vehicle>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}`);
  const insuranceQ = useApiQuery<InsuranceStatus>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/insurance-status`);
  const rwQ = useApiQuery<Page<RoadworthinessTest>>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/roadworthiness?limit=10`);

  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [transferAction, setTransferAction] = useState<TransferAction>(null);
  const [lookupId, setLookupId] = useState('');
  const [buyerRef, setBuyerRef] = useState('');
  const [declaredValue, setDeclaredValue] = useState('');
  const [initiatedBy, setInitiatedBy] = useState<'SELLER' | 'BUYER'>('SELLER');
  const [side, setSide] = useState<'SELLER' | 'BUYER'>('SELLER');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = async (fn: () => Promise<Transfer>) => {
    setBusy(true);
    setError(null);
    try {
      const t = await fn();
      setTransfer(t);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const v = vehicleQ.data;

  return (
    <Modal title={v ? `${v.regNo} — ${v.make} ${v.model}` : regNo} onClose={onClose} wide>
      {vehicleQ.error ? <ErrorNote message={vehicleQ.error} /> : null}
      {vehicleQ.loading ? <Spinner /> : null}
      {v ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={v.status} />} />
            <Detail label="Vehicle id (for PSV permits)" value={v.id} mono />
            <Detail label="VIN" value={v.vin} mono />
            <Detail label="Year" value={String(v.year)} />
            <Detail label="Owner" value={`${v.ownerType} · ${v.ownerRef}`} />
            {v.stolenCaseRef ? <Detail label="Stolen case ref" value={v.stolenCaseRef} mono /> : null}
            {v.stolenFlaggedAt ? <Detail label="Flagged stolen" value={formatDateTime(v.stolenFlaggedAt)} /> : null}
          </div>

          <div>
            <div className="field-label">Insurance status (external-provider seam)</div>
            {insuranceQ.loading ? (
              <Spinner label="Checking insurer…" />
            ) : insuranceQ.data ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <Detail label="Insured" value={insuranceQ.data.insured ? 'Yes' : 'No'} />
                {insuranceQ.data.insurer ? <Detail label="Insurer" value={insuranceQ.data.insurer} /> : null}
                {insuranceQ.data.policyNo ? <Detail label="Policy no." value={insuranceQ.data.policyNo} mono /> : null}
                {insuranceQ.data.expiresAt ? <Detail label="Expires" value={formatDate(insuranceQ.data.expiresAt)} /> : null}
              </div>
            ) : null}
          </div>

          <div>
            <div className="field-label">Roadworthiness history</div>
            {rwQ.loading ? (
              <Spinner />
            ) : (rwQ.data?.items.length ?? 0) === 0 ? (
              <div className="text-xs text-slate-400">No roadworthiness tests on file.</div>
            ) : (
              <div className="space-y-1.5">
                {rwQ.data!.items.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5 text-sm">
                    <span className="text-xs text-slate-500">Station {t.stationId} · {formatDate(t.inspectedAt)}</span>
                    <StatusBadge status={t.result} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700">Ownership transfer</div>
              {!transfer && canAct ? (
                <div className="flex gap-2">
                  <button className="btn-secondary !py-1 !text-xs" onClick={() => setTransferAction('lookup')}>
                    Look up by ID
                  </button>
                  <button className="btn-secondary !py-1 !text-xs" onClick={() => setTransferAction('create')}>
                    Initiate transfer
                  </button>
                </div>
              ) : null}
            </div>

            {error ? (
              <div className="mb-2">
                <ErrorNote message={error} />
              </div>
            ) : null}

            {!canAct ? <RoleNotice roles={OFFICER_ROLES} /> : null}

            {transferAction === 'lookup' && !transfer ? (
              <div className="flex items-end gap-2">
                <Field label="Transfer id" className="flex-1">
                  <input className="field-input" value={lookupId} onChange={(e) => setLookupId(e.target.value)} />
                </Field>
                <button
                  className="btn-secondary"
                  disabled={busy || !lookupId.trim()}
                  onClick={() =>
                    call(() => api.get<Transfer>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/transfers/${lookupId.trim()}`))
                  }
                >
                  {busy ? 'Looking up…' : 'Fetch'}
                </button>
              </div>
            ) : null}

            {transferAction === 'create' && !transfer ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Buyer NIN / business id">
                  <input className="field-input" value={buyerRef} onChange={(e) => setBuyerRef(e.target.value)} />
                </Field>
                <Field label="Declared value (SCR)">
                  <input className="field-input" type="number" value={declaredValue} onChange={(e) => setDeclaredValue(e.target.value)} />
                </Field>
                <Field label="Initiated by">
                  <select className="field-input" value={initiatedBy} onChange={(e) => setInitiatedBy(e.target.value as 'SELLER' | 'BUYER')}>
                    <option value="SELLER">Seller</option>
                    <option value="BUYER">Buyer</option>
                  </select>
                </Field>
                <div className="flex items-end">
                  <button
                    className="btn-primary"
                    disabled={busy || !buyerRef.trim() || !declaredValue}
                    onClick={() =>
                      call(() =>
                        api.post<Transfer>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/transfers`, {
                          buyerRef: buyerRef.trim(),
                          declaredValue: Number.parseFloat(declaredValue),
                          initiatedBy,
                        }),
                      )
                    }
                  >
                    {busy ? 'Creating…' : 'Create transfer'}
                  </button>
                </div>
              </div>
            ) : null}

            {transfer ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                  <Detail label="Status" value={<StatusBadge status={transfer.status} />} />
                  <Detail label="Seller" value={transfer.sellerRef} mono />
                  <Detail label="Buyer" value={transfer.buyerRef} mono />
                  <Detail label="Declared value" value={scr(transfer.declaredValue)} />
                  <Detail label="Duty (3%)" value={scr(transfer.dutyAmount)} />
                  <Detail label="Duty payment ref" value={transfer.payIntentRefNo ?? '—'} mono />
                  <Detail label="Seller confirmed" value={transfer.sellerConfirmedAt ? formatDateTime(transfer.sellerConfirmedAt) : '—'} />
                  <Detail label="Buyer confirmed" value={transfer.buyerConfirmedAt ? formatDateTime(transfer.buyerConfirmedAt) : '—'} />
                </div>
                {canAct && transfer.status === 'PENDING' ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <Field label="Confirm as">
                      <select className="field-input !w-auto" value={side} onChange={(e) => setSide(e.target.value as 'SELLER' | 'BUYER')}>
                        <option value="SELLER">Seller</option>
                        <option value="BUYER">Buyer</option>
                      </select>
                    </Field>
                    <button
                      className="btn-success"
                      disabled={busy}
                      onClick={() =>
                        call(() =>
                          api.post<Transfer>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/transfers/${transfer.id}/confirm`, { side }),
                        )
                      }
                    >
                      Confirm side
                    </button>
                    <button
                      className="btn-danger"
                      disabled={busy}
                      onClick={() =>
                        call(() => api.post<Transfer>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/transfers/${transfer.id}/cancel`))
                      }
                    >
                      Cancel transfer
                    </button>
                  </div>
                ) : null}
                {canAct && transfer.status === 'CONFIRMED' && !transfer.payIntentRefNo ? (
                  <button
                    className="btn-secondary"
                    disabled={busy}
                    onClick={() =>
                      call(() =>
                        api.post<Transfer>(`/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/transfers/${transfer.id}/retry-duty-payment`),
                      )
                    }
                  >
                    Retry duty payment (recovery)
                  </button>
                ) : null}
                <button
                  className="text-xs text-slate-400 underline"
                  onClick={() => {
                    setTransfer(null);
                    setTransferAction(null);
                  }}
                >
                  Clear
                </button>
              </div>
            ) : null}

            {!transferAction && !transfer && !canAct ? (
              <div className="text-xs text-slate-400">No transfer loaded.</div>
            ) : null}
          </div>

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Registered {formatDateTime(v.createdAt)} · last updated {formatDateTime(v.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
